import React, { useEffect, useRef } from 'react'
import useKeyPress from '../../../../hooks/useKeyPress'
import useOnClickOutside from '../../../../hooks/useOnClickOutside'

const useKeyedList = <T extends unknown>({
  items,
  handleEnter,
  defaultIsActive
}: {
  items: T[]
  handleEnter?: (item: T) => void | Promise<void>
  defaultIsActive?: boolean
}) => {
  const [isActive, setIsActive] = React.useState(defaultIsActive)
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)

  const arrowDown = useKeyPress('ArrowDown')
  const arrowUp = useKeyPress('ArrowUp')

  const ref = useRef(null)
  useOnClickOutside(ref, () => setIsActive(false))

  useEffect(() => {
    if (arrowUp && isActive && highlightedIndex > 0) {
      setHighlightedIndex((prev) => prev - 1)
    }
  }, [arrowUp])

  useEffect(() => {
    if (arrowDown && isActive && highlightedIndex < items.length - 1) {
      setHighlightedIndex((prev) => prev + 1)
    }
  }, [arrowDown])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown'].indexOf(event.code) > -1) {
        event.preventDefault()
      }

      // Set the selected index when selected with enter key, optional handler callback
      if (isActive && event.code === 'Enter') {
        setSelectedIndex(highlightedIndex)

        if (handleEnter) {
          handleEnter(items[highlightedIndex])
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, highlightedIndex])

  return {
    isActive,
    setIsActive,
    highlightedIndex,
    setHighlightedIndex,
    selectedIndex,
    setSelectedIndex,
    ref
  }
}

export default useKeyedList
